import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ApiPoller from '../../../atoms/api-poller';
import ProgressBar from '../../../atoms/progress-bar';
import { pluralise } from '../../../../utils/form-utils';
import { toCurrency } from '../../../../utils/react-utils';
import Logo from '../../../atoms/logo';
import styles from './styles.scss';
import LinkButtonA from '../../../molecules/link-button/LinkButtonA';
import IframeDialog from '../../../atoms/iframe-dialog';
import MobileIdentifier from '../../../atoms/mobile-identifier/MobileIdentifier';

class Leaderboard extends React.Component {
    constructor(props) {
        super(props);

        this.receiveApiEvent = this.receiveApiEvent.bind(this);
        this.getProgressBars = this.getProgressBars.bind(this);
        this.sortGroups = this.sortGroups.bind(this);
        this.getShowMore = this.getShowMore.bind(this);
        this.getApiPoller = this.getApiPoller.bind(this);
        this.fetchGroups = this.fetchGroups.bind(this);
        this.handleShowMore = this.handleShowMore.bind(this);
        this.getPageUrl = this.getPageUrl.bind(this);
        this.handleClickEdit = this.handleClickEdit.bind(this);
        this.iframeDialogSizeHandler = this.iframeDialogSizeHandler.bind(this);
        this.handleMessage = this.handleMessage.bind(this);

        this.state = {
            showMore: false,
            allGroups: [],
            groups: [],
            showIframe: false,
            // smallIframeDialog is true when dialog is small size to display success message
            smallIframeDialog: false,
        };
    }

    componentDidMount() {
        this.fetchGroups();
    }

    componentDidUpdate() {
        // make sure we make up for any failed groups api calls
        // otherwise leaderboard won't show.
        const { groups } = this.state;
        if (!groups) {
            this.fetchGroups();
        }
    }

    handleShowMore() {
        const { showMore } = this.state;
        this.setState(() => ({
            showMore: !showMore,
        }));
    }

    handleClickEdit(shouldOpen) {
        this.setState(() => ({
            showIframe: shouldOpen,
        }));
    }

    // successMessage.cshtml will post message:   "successMessage"
    handleMessage(event) {
        const messageFromSender = event.data;
        if (messageFromSender.message === 'successMessage') {
            this.setState(() => ({
                smallIframeDialog: true,
            }));
        }
    }

    getPageUrl(groupSlug) {
        const { baseurl, appealslug } = this.props;
        const groupUrl = new URL(baseurl);

        let path = groupUrl.pathname;

        // get rid of trailing slashes
        if (path.endsWith('/')) {
            path = path.substring(0, path.lastIndexOf('/'));
        }

        // The leaderboard is in a group page
        if (!path.endsWith(appealslug)) {
            // last '/' should be right before the groupslug as we got rid of trailing slashes
            path = path.substring(0, path.lastIndexOf('/'));
        }

        groupUrl.pathname = `${path}/${groupSlug}`;
        return groupUrl;
    }

    getProgressBars() {
        const { showMore, groups } = this.state;
        const { isMobile } = this.props;
        let groupsToRender = groups;
        if (!showMore) {
            groupsToRender = groupsToRender.slice(0, 3);
        }

        return groupsToRender.map((g) => {
            const sum = g.sum ?? 0;
            const count = g.count ?? 0;

            return (
                <div key={g.key}>
                    <span className={styles['title-column']}>
                        <div>
                            <div>
                                <Logo image={g.logo} />
                            </div>
                            <span>
                                <h6>
                                    {isMobile ? <a href={g.pageUrl}>{g.title.length > 20 ? `${g.title.substring(0, 17)}...` : g.title}</a>
                                        : <a href={g.pageUrl}>{g.title.length > 27 ? `${g.title.substring(0, 24)}...` : g.title}</a>}

                                </h6>
                            </span>
                        </div>
                    </span>
                    <span className={styles['progress-column']}>
                        <p>
                            Raised
                            {` ${toCurrency(sum)}`}
                            {` from  ${count} ${pluralise(count, 'donor', 'donors')}`}
                        </p>
                        <div>
                            <ProgressBar
                                key={g.key}
                                id={g.key}
                                max={g.goal}
                                current={sum}
                            />
                        </div>
                    </span>
                </div>
            );
        });
    }

    getApiPoller() {
        const { donationquery, donationtoken } = this.props;
        const { allGroups } = this.state;

        if (allGroups.length > 0) {
            return (
                <ApiPoller
                    queries={[donationquery]}
                    callback={this.receiveApiEvent}
                    token={donationtoken}
                />
            );
        }

        return null;
    }

    getShowMore() {
        const { groups, showMore } = this.state;
        let morelinkText = 'See All Teams';
        if (showMore) {
            morelinkText = 'Show Less';
        }
        return groups.length > 3
            ? (
                <div className={styles['show-more']}>
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={this.handleShowMore}
                    >
                        {morelinkText}
                        <span>
                            {showMore
                                ? (
                                    <svg width="14" height="13" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 8l-6 6 1.41 1.41 4.59-4.58 4.59 4.58 1.41-1.41z" fill="#6A6767" />
                                    </svg>
                                )
                                : (
                                    <svg width="10" height="6" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.59 0.589996L6 5.17L1.41 0.589996L0 2L6 8L12 2L10.59 0.589996Z" fill="#6A6767" />
                                    </svg>
                                )}
                        </span>
                    </span>
                </div>
            )
            : <div style={{ width: '100%', height: '21px' }} />;
    }

    fetchGroups() {
        const { contextkey, baseurl } = this.props;
        const { host, protocol } = new URL(baseurl);

        // get Top groups for the specified appeal
        fetch(`${protocol}//${host}/odata/groups?$apply=filter((ContextKey eq '${contextkey}') and (IsDisabled eq false))`)
            .then((response) => response.json())
            .then((json) => json.value)
            .then((list) => {
                this.setState(() => ({
                    allGroups: list,
                }));
            });
    }

    convertLogo(logo) {
        const { baseurl } = this.props;
        const imageUrl = new URL(baseurl);
        imageUrl.pathname = logo;
        imageUrl.search = '';

        return imageUrl;
    }

    receiveApiEvent(response, statusCode) {
        if (statusCode === 200) {
            const donations = response.value;
            if (Array.isArray(donations)) {
                const groups = this.sortGroups(donations)
                    .map((g) => ({
                        key: g.key,
                        title: g.title,
                        logo: this.convertLogo(g.logoUrl).href,
                        goal: g.goal,
                        pageUrl: this.getPageUrl(g.path),
                        sum: parseFloat(g.total),
                        count: parseInt(g.count, 10),
                    }));
                this.setState(() => ({
                    groups,
                }));
            }
        }
    }

    sortGroups(donations) {
        const { allGroups, showMore } = this.state;

        // viewmodel essentially
        const convert = (g, total, count) => ({
            key: g.Key,
            title: g.Title,
            logoUrl: g.LogoUrl,
            goal: g.Goal,
            path: g.Path,
            total,
            count,
        });
        let max = 6;
        if (showMore) max = allGroups.length;

        const groupWithTotals = donations
            .filter((tg) => allGroups.map((g) => g.Key).includes(tg.group_key))
            .map((tg) => {
                const group = allGroups.find((g) => g.Key === tg.group_key);

                return convert(group, tg.Total, tg.Count);
            });

        const apiGroupsNeedPadding = groupWithTotals.length < max;
        const paddingExists = allGroups.length > groupWithTotals.length;
        const noApiGroups = groupWithTotals.length < 1;

        // if there is less than the total amount of groups to show
        // let's pad the array with groups that didn't show up through
        // the Groups API response only if the total number of groups > top
        if ((apiGroupsNeedPadding && paddingExists) || noApiGroups) {
            const moreGroupsThanMax = allGroups.length > max;

            let paddingNeeded = 0;
            if (moreGroupsThanMax) {
                paddingNeeded = max - groupWithTotals.length;
            } else {
                // allGroups.length then becomes the max here
                // as we don't have enough groups to padding until max count
                paddingNeeded = allGroups.length - groupWithTotals.length;
            }

            const existingGroupKeys = groupWithTotals.map((g) => g.key);
            const potentialPadding = allGroups.filter((g) => !existingGroupKeys.includes(g.Key));
            const padding = potentialPadding
                .slice(0, paddingNeeded)
                .map((g) => convert(g, 0, 0));

            return groupWithTotals.concat(padding);
        }

        return groupWithTotals;
    }

    iframeDialogSizeHandler() {
        this.setState({
            smallIframeDialog: false,
        }, () => {
            // listen if there is any message post by other window
            if (window.addEventListener) {
                window.addEventListener('message', this.handleMessage);
            } else {
                // to support IE versions before IE 9
                window.attachEvent('onmessage', this.handleMessage);
            }
        });
    }

    render() {
        const {
            groups, showIframe, smallIframeDialog,
        } = this.state;
        const {
            contextkey, baseurl, isMobile,
        } = this.props;
        const { host, protocol } = new URL(baseurl);

        const form = document.getElementById('leaderBoardPanelPlaceHolder');
        const hideClass = !form || groups.length < 1 ? styles.hide : '';

        const iframeClass = `${styles['leaderboard-iframe-style']} ${smallIframeDialog ? styles.small : styles.large} ${isMobile ? styles['is-mobile'] : ''}`;

        const largeIframeDialogStyle = {
            minWidth: isMobile ? '90%' : '691px',
            minHeight: '80%',
        };

        const smallIframeDialogStyle = {
            minWidth: isMobile ? '100%' : '691px',
            minHeight: '47%',
        };

        const dialogContent = (
            <iframe
                id={`leaderboard-iframe-${contextkey}`}
                src={`${protocol}//${host}/Campaigns/Groups/Signup?contextkey=${contextkey}`}
                title="Iframe Dialog"
                onLoad={this.iframeDialogSizeHandler}
                className={iframeClass}
            />
        );

        const groupIframe = showIframe
            ? (
                <IframeDialog
                    clickHandler={this.handleClickEdit}
                    content={dialogContent}
                    paperPropsStyle={smallIframeDialog
                        ? { ...smallIframeDialogStyle, backgroundColor: '#F2F2F2' } : { ...largeIframeDialogStyle, backgroundColor: '#F2F2F2' }}
                />
            )
            : null;

        const leaderboard = (
            <div id="GroupLeaderBoard">
                <div
                    id="spacer"
                    style={{
                        width: '100%',
                        height: '24px',
                        border: 'none',
                        backgroundColor: '#fff',
                    }}
                />

                <div className={`${styles.leaderboard} ${hideClass}`}>
                    <div className={styles.header}>
                        <h6>Our teams</h6>
                        <LinkButtonA
                            buttonclass={styles.editGroupButton}
                            clickHandler={() => this.handleClickEdit(true)}
                        >
                            Register/Log in
                        </LinkButtonA>
                        {groupIframe}
                    </div>
                    {this.getApiPoller()}
                    <div className={styles['group-panel']} data-testid="progress-bars">
                        {this.getProgressBars()}
                    </div>
                    {this.getShowMore()}
                </div>
            </div>
        );

        if (form === null) {
            return leaderboard;
        }

        return ReactDOM.createPortal(
            leaderboard,
            form,
        );
    }
}

Leaderboard.propTypes = {
    contextkey: PropTypes.string.isRequired,
    appealslug: PropTypes.string.isRequired,
    baseurl: PropTypes.string,
    donationquery: PropTypes.string.isRequired,
    donationtoken: PropTypes.string.isRequired,
    isMobile: PropTypes.bool.isRequired,
};

Leaderboard.defaultProps = {
    baseurl: window.location.href,
};

export default MobileIdentifier(Leaderboard);
